import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Lock, Download, Info, ExternalLink, Eye, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { ImportTemplateConfirmationModal } from '@/components/templates/ImportTemplateConfirmationModal';
import { useHubTemplates, useImportHubTemplate, useTemplateDetail, type HubTemplate } from '@/api/hooks/useAttestations';

// ── CSA Attribution component (rendered on CAIQ rows) ─────────────────────────

function CsaAttribution() {
  return (
    <p className="text-[10px] text-text-muted mt-1">
      © Cloud Security Alliance — CC BY-SA 4.0 ·{' '}
      <a
        href="https://cloudsecurityalliance.org/research/cloud-controls-matrix/"
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-text-secondary"
      >
        cloudsecurityalliance.org
      </a>
    </p>
  );
}

// ── Hub template preview modal ────────────────────────────────────────────────

function PreviewSectionRow({ name, questionCount, questions }: {
  name: string;
  questionCount: number;
  questions: { id: string; question_text: string; guidance_text: string | null; response_type: string; is_required: boolean }[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border-subtle rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-bg-surface hover:bg-bg-hover transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {open
            ? <ChevronDown className="h-3.5 w-3.5 text-text-secondary shrink-0" />
            : <ChevronRight className="h-3.5 w-3.5 text-text-secondary shrink-0" />}
          <span className="text-sm font-medium text-text-primary">{name}</span>
        </div>
        <Badge variant="muted" className="text-[10px] py-0">{questionCount}q</Badge>
      </button>
      {open && (
        <div className="divide-y divide-border-subtle bg-bg-canvas">
          {questions.map((q, idx) => (
            <div key={q.id} className="px-4 py-2.5 flex items-start gap-3">
              <span className="text-xs text-text-muted font-mono w-6 shrink-0 mt-0.5">{idx + 1}.</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary">{q.question_text}</p>
                {q.guidance_text && (
                  <p className="text-xs text-text-secondary mt-0.5">{q.guidance_text}</p>
                )}
              </div>
              <span className="text-[10px] text-text-muted shrink-0 mt-0.5 font-mono">{q.response_type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HubTemplatePreviewModal({ template, open, onOpenChange }: {
  template: HubTemplate | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: detail, isLoading } = useTemplateDetail(open ? template?.id : undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg leading-snug">
            {template?.name ?? 'Template Preview'}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3 py-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !detail ? (
          <p className="py-6 text-center text-sm text-text-secondary">Failed to load template details.</p>
        ) : (
          <div className="space-y-4 py-1">
            {/* Meta row */}
            <div className="flex flex-wrap gap-2 text-xs text-text-secondary">
              {detail.framework && <Badge variant="muted" className="text-[10px]">{detail.framework}</Badge>}
              <span className="font-mono">{detail.total_questions} questions</span>
              <span className="font-mono">v{detail.version}</span>
              {template?.requires_license && (
                <Badge variant="amber" className="text-[10px] flex items-center gap-1">
                  <Lock className="h-2.5 w-2.5" /> License required
                </Badge>
              )}
            </div>

            {detail.description && (
              <p className="text-sm text-text-secondary">{detail.description}</p>
            )}

            {/* Sections */}
            {detail.sections.length === 0 ? (
              <p className="text-sm text-text-secondary italic">No sections found.</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  {detail.sections.length} section{detail.sections.length !== 1 ? 's' : ''}
                </p>
                {detail.sections.map((s) => (
                  <PreviewSectionRow
                    key={s.id}
                    name={s.name}
                    questionCount={s.questions.length}
                    questions={s.questions}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Template card ─────────────────────────────────────────────────────────────

function HubTemplateCard({
  template,
  onImport,
  onAttest,
  onPreview,
}: {
  template: HubTemplate;
  onImport: (t: HubTemplate) => void;
  onAttest: (t: HubTemplate) => void;
  onPreview: (t: HubTemplate) => void;
}) {
  const navigate = useNavigate();
  const requiresLicense = template.requires_license;
  // Licensed-Content Slice 2 (D-LC.2-5): the surfaced field is now has_license_grant (an active
  // per-tenant grant), not an attestation. Tile stays visible; import is grant-gated.
  const hasGrant = template.has_license_grant;
  const canImport = !requiresLicense || hasGrant;
  const isVsaq = template.framework === 'VSAQ';
  const isCaiq = template.framework === 'CAIQ';
  const alreadyImported = template.tenant_imported_template_id !== null;

  const versionString = `v${template.version} · VeriGRC content rev ${template.content_revision}`;

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base leading-tight">{template.name}</CardTitle>
            {isCaiq && <CsaAttribution />}
          </div>
          <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
            {template.framework && (
              <Badge variant="muted" className="text-[10px] py-0">{template.framework}</Badge>
            )}
            {requiresLicense && (
              <Badge variant="amber" className="text-[10px] py-0 flex items-center gap-1">
                <Lock className="h-2.5 w-2.5" />
                {hasGrant ? 'Licensed' : 'License Required'}
              </Badge>
            )}
            {isVsaq && (
              <Badge variant="sky" className="text-[10px] py-0">Coming Soon</Badge>
            )}
            {alreadyImported && (
              <Badge variant="emerald" className="text-[10px] py-0">Imported</Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 space-y-3">
        {template.description && (
          <p className="text-sm text-text-secondary line-clamp-3">{template.description}</p>
        )}

        <div className="flex flex-wrap gap-3 text-xs text-text-secondary font-mono">
          {template.total_questions > 0 && (
            <span>{template.total_questions} questions</span>
          )}
          <span>{versionString}</span>
          {template.vendor_type_target.length > 0 && (
            <span>{template.vendor_type_target.slice(0, 2).join(', ')}{template.vendor_type_target.length > 2 ? ` +${template.vendor_type_target.length - 2}` : ''}</span>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onPreview(template)}
            className="shrink-0"
          >
            <Eye className="mr-1.5 h-3.5 w-3.5" />
            View
          </Button>
          {alreadyImported ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/templates/${template.tenant_imported_template_id}`)}
              className="w-44 shrink-0"
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Open imported copy
            </Button>
          ) : canImport ? (
            <Button
              size="sm"
              onClick={() => onImport(template)}
              className="w-44 shrink-0"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Import
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAttest(template)}
              className="w-44 shrink-0"
            >
              <Lock className="mr-1.5 h-3.5 w-3.5" />
              License & Import
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function HubTemplatesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [licensingBlocked, setLicensingBlocked] = useState<HubTemplate | null>(null);
  const [confirmModal, setConfirmModal] = useState<HubTemplate | null>(null);
  const [previewModal, setPreviewModal] = useState<HubTemplate | null>(null);

  const { data: templates, isLoading } = useHubTemplates();
  const importMutation = useImportHubTemplate();
  useEffect(() => { document.title = 'Assessment Library — VeriGRC'; }, []);

  const filtered = (templates ?? []).filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.description ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (t.framework ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  // Assessment Lines Slice C — client-side group-by line_id over the (published-filtered) browse.
  // A line section appears iff it has ≥1 published member (composition — nothing added). Ungrouped
  // templates (line_id NULL) render standalone after the sections. Zero lines → degradation to today.
  const ungrouped: HubTemplate[] = [];
  const groupMap = new Map<string, { name: string; description: string | null; sortOrder: number; items: HubTemplate[] }>();
  for (const t of filtered) {
    if (t.line_id && t.line_name) {
      const g = groupMap.get(t.line_id) ?? { name: t.line_name, description: t.line_description, sortOrder: t.line_sort_order ?? 0, items: [] };
      g.items.push(t);
      groupMap.set(t.line_id, g);
    } else {
      ungrouped.push(t);
    }
  }
  const lineSections = [...groupMap.values()].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

  const renderCard = (t: HubTemplate) => (
    <HubTemplateCard
      key={t.id}
      template={t}
      onImport={handleImportClick}
      onAttest={(template) => setLicensingBlocked(template)}
      onPreview={(template) => setPreviewModal(template)}
    />
  );

  function handleImportClick(t: HubTemplate) {
    setConfirmModal(t);
  }

  async function handleImportConfirm(t: HubTemplate) {
    try {
      const result = await importMutation.mutateAsync(t.id);
      setConfirmModal(null);
      if (result?.id) {
        toast.success(`"${t.name}" imported to your Assessment Templates.`);
        navigate(`/templates/${result.id}`);
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { errors?: { code?: string } } } };
      const code = e?.response?.data?.errors?.code;
      // Licensed-Content Slice 3 (D-LC.2-1 cleanup): the deploy-skew window is closed, so the
      // legacy LICENSE_ATTESTATION_REQUIRED branch is removed — only LICENSE_GRANT_REQUIRED maps
      // to the contact message now.
      if (code === 'LICENSE_GRANT_REQUIRED') {
        setConfirmModal(null);
        setLicensingBlocked(t);
      } else {
        toast.error('Import failed. Please try again.');
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Assessment Library</h1>
          <p className="text-sm text-text-secondary mt-1">
            Platform-provided assessment questionnaires. Import a copy to customise for your vendors.
          </p>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2.5 rounded-md border border-border-subtle bg-bg-surface p-3 text-sm text-text-secondary">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-accent-cyan" />
        <span>
          This template includes content provided through VeriGRC. Your use, modification, and any
          content you add remain subject to the applicable template license and the VeriGRC platform
          terms. Your organization is responsible for complying with those terms and any rights it
          holds in content it adds.
        </span>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
        <Input
          placeholder="Search hub templates…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <Skeleton className="h-5 w-40" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-8 w-24 mt-3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={search ? Search : Download}
          title={search ? 'No templates match your search' : 'No hub templates available'}
          description={search ? 'Try a different search term.' : 'Hub templates will appear here when published.'}
        />
      ) : lineSections.length === 0 ? (
        // Degradation (AC-3): no assessment lines → byte-equivalent to the pre-Slice-C flat grid.
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(renderCard)}
        </div>
      ) : (
        // Grouped: line sections first (header + optional subtitle, ordered sort_order→name), then
        // ungrouped standalone tiles — NO fabricated "Other" header (D-AL.C-1). Card unchanged in-group.
        <div className="space-y-8">
          {lineSections.map((g) => (
            <section key={g.name} className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">{g.name}</h2>
                {g.description && <p className="text-sm text-text-secondary mt-0.5">{g.description}</p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.items.map(renderCard)}
              </div>
            </section>
          ))}
          {ungrouped.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ungrouped.map(renderCard)}
            </div>
          )}
        </div>
      )}

      {/* Hub template read-only preview */}
      <HubTemplatePreviewModal
        template={previewModal}
        open={previewModal !== null}
        onOpenChange={(open) => { if (!open) setPreviewModal(null); }}
      />

      {/* D-VA-2 import confirmation modal */}
      <ImportTemplateConfirmationModal
        template={confirmModal}
        open={confirmModal !== null}
        onOpenChange={(open) => { if (!open) setConfirmModal(null); }}
        onConfirm={handleImportConfirm}
        isPending={importMutation.isPending}
      />

      {/* Licensed-Content Slice 2 (D-LC.2-1): the contact-for-licensing message — replaces the
          retired attestation modal. The tile stays visible; import is blocked pending a grant. */}
      {licensingBlocked && (
        <Dialog open onOpenChange={(o) => { if (!o) setLicensingBlocked(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Licensing required</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-text-secondary">
              <strong>{licensingBlocked.name}</strong> requires a license before it can be imported.
              Contact the content provider for licensing, or contact our sales team.
            </p>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setLicensingBlocked(null)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
